import { multiplyAndRemoveDecimal } from "@/utils/utils";
import type { SourceData } from './InquirySources/inquirySourceState';
import { ReservationStatusDto } from "./ReservationStatus";
import { ProgramBooking, ProgramBookingState } from "./model.Programs";
import { getDisplayName, isReservationExistingPerson } from "./model.ReservationPerson";
import { ReservationQuoteDto } from "./model.ReservationQuotes";
import { buildSaveReservationRequest } from "./model.Reservations";
import { ReservationArrivalTransferDetailsDto, ReservationDepartureTransferDetailsDto } from "./model.TransferDetails";
import { initializeModel2 } from './model.aggregates';
import type { ModelContext } from "./model.context";
import { initializeModel0 } from './model.createState';
import { RatesContainerDto, ReservationPosItemDto } from './model.hydration';
import { initializePriceChecks } from "./model.pricing";
import { ReservationState } from './model.roomsAndTotals';
import { ReservationNote, RoomBlockDateDto, ServerPersonWithCreditsDto, Voucher } from './model.saveAndHistory';
import { initializeModel1 } from './model.subscriptions';
import { RevenueItemDto } from "./revenueHydration";
import {
    MergeableRoomCluster,
    ReservationDiffContainerDto,
    ReservationLogEntryContainerDto,
    ReservationPaymentDto,
    ReservationRefundDto,
    RoomBooking,
    RoomType,
    SaveReservationRequestVoucher
} from "./types";
import { refreshDiffs, reloadPosItems } from "./utils";
export type { DiffTransaction, RatePlanKey } from './model.datesAndProfiles';
export type { RatePlanFormFieldDto, RatePlanFormFieldOptionDto, RatesBaseRateDto, RatesContainerDto, RatesRateDiscountDto, RatesRateMultiplierDto, RatesRatePlanDto, ReservationPosItem, ReservationPosItemDto } from './model.hydration';
export { getDefaultPeople } from './model.roomsAndTotals';
export type { ReservationState } from './model.roomsAndTotals';
export { fetchHistoricalVersion, fetchVersionHistory, pollForChangeRequest, revertToVersion } from './model.saveAndHistory';
export type {
    ArrivalTransferDetailsState,
    DepartureTransferDetailsState,
    NewPersonDto,
    PersonCredit,
    PersonCreditDto,
    PersonDto,
    ReservationLogEntryContainer,
    ReservationNote,
    ReservationPayment,
    ReservationPaymentTransaction,
    ReservationProfileCacheDto,
    ReservationRefund,
    ReservationRefundTransaction,
    RoomBlockDate,
    RoomBlockDateRoom,
    RoomBlockDatesContainer,
    ServerPersonCreditDto,
    ServerPersonWithCreditsDto,
    Voucher
} from './model.saveAndHistory';
export { cancelGlobalModelLoad, clearGlobalModel, getGlobalModel, retryGlobalModel, setGlobalModel, setGlobalModelAsync } from './model.singleton';
export * from './types';
export { buildSaveReservationRequest, getDisplayName, isReservationExistingPerson, multiplyAndRemoveDecimal, refreshDiffs, reloadPosItems };
export type { ProgramBookingState, RevenueItemDto };
/*
    One goal of the model is to sync the start and end dates:
    * If the rooms all check-in and check-out on the same date
    * and the programs are all starting on check-in date + 1 day, and end on the check-out date
    * Then the overall reservation start and end dates can be adjusted and all the rooms and programs should follow
*/
export async function fetchModel(url: string, signal?: AbortSignal): Promise<ReservationState> {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error('Could not load the reservation. Please retry.');
    const reservation = await response.json();
    return createModel(
        reservation.reservationId,
        reservation.reservationNumber,
        reservation.personId,
        reservation.billingContactId ?? null,
        reservation.roomBookings,
        reservation.programBookings,
        reservation.appliedVouchers,
        reservation.vouchers,
        reservation.payments,
        reservation.refunds,
        reservation.revenue,
        reservation.posItems,
        reservation.reservationStatus,
        reservation.quote,
        reservation.arrivalTransferDetails,
        reservation.departureTransferDetails,
        reservation.notes,
        reservation.logEntries,
        reservation.roomTypes,
        reservation.roomBlockDates,
        reservation.revenueLockDate,
        reservation.rates,
        reservation.selectedRatePlanId,
        reservation.selectedRatePlanVersion,
        reservation.ratePlanFormResponseJson,
        reservation.orderDiffs,
        reservation.preloadedProfiles,
        reservation.preloadedContacts,
        reservation.mergeableRoomClusters,
        { value: reservation.inquirySource, isForm: reservation.inquirySourceAutomatic, choices: reservation.inquirySourceChoices }
    );
}
export function createModel(reservationId: string,
    reservationNumber: number | null,
    personId: string | null,
    billingContactId: string | null,
    roomBookings: RoomBooking[],
    programBookings: ProgramBooking[],
    appliedVouchers: SaveReservationRequestVoucher[],
    vouchers: Voucher[],
    payments: ReservationPaymentDto[],
    refunds: ReservationRefundDto[],
    revenue: RevenueItemDto[],
    posItems: ReservationPosItemDto[],
    reservationStatus: ReservationStatusDto | null,
    quoteDto: ReservationQuoteDto | null,
    arrivalTransferDetails: ReservationArrivalTransferDetailsDto | null,
    departureTransferDetails: ReservationDepartureTransferDetailsDto | null,
    notes: ReservationNote[],
    logEntries: ReservationLogEntryContainerDto[],
    roomTypes: {
        [key: string]: RoomType;
    },
    roomBlockDates: RoomBlockDateDto[],
    revenueLockDate: string | null,
    rates: RatesContainerDto,
    selectedRatePlanId: string,
    selectedRatePlanVersion: number,
    ratePlanFormResponseJson: string | null,
    orderDiffs: ReservationDiffContainerDto[],
    preloadedProfiles: {
        [personId: string]: ServerPersonWithCreditsDto;
    } | null = null,
    preloadedContacts: {
        [contactId: string]: any;
    } | null = null,
    mergeableRoomClusters: MergeableRoomCluster[] | null = null,
    inquirySourceData?: SourceData): ReservationState {
    const context = {
        reservationId, reservationNumber, personId, billingContactId, roomBookings, programBookings, appliedVouchers, vouchers, payments, refunds, revenue, posItems, reservationStatus, quoteDto, arrivalTransferDetails, departureTransferDetails, notes, logEntries, roomTypes, roomBlockDates, revenueLockDate, rates, selectedRatePlanId, selectedRatePlanVersion, ratePlanFormResponseJson, orderDiffs, preloadedProfiles, preloadedContacts, mergeableRoomClusters, inquirySourceData
    } as ModelContext;
    initializePriceChecks(context);
    initializeModel0(context);
    initializeModel1(context);
    initializeModel2(context);
    return context.reservation;
}
